import DateRangePicker from '@wojtekmaj/react-daterange-picker/dist/DateRangePicker';
import moment from 'moment';
import { PropTypes } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import Dropzone from '../../../../components/Input/Dropzone/Dropzone';
import useInvoiceOptions from '../../../../hooks/invoices/useInvoiceOptions';
import useDispatchNotification from '../../../../hooks/notifications/useDispatchNotification';
import { getCampaignsByUserID } from '../../../../services/adminService';
import CreateButton from '../../../Buttons/CreateButton';
import UserSelector from '../../../Input/UserSelector/UserSelector';
import InputWithLabel from '../../../Input/WithLabel/InputWithLabel';
import './DataRangerPicker.css';

const InvoiceForm = ({
    onSubmit,
    isLoading,
    setIsLoading,
    isSuccess,
    setIsSuccess,
    setFormInvoice,
    formInvoice,
    inputFileRef,
}) => {
    const {
        invoiceTypesOptions,
        methodOptions,
        initialInvoice,
        initialPeriod,
    } = useInvoiceOptions();
    const [campaignsOptions, setCampaignsOptions] = useState([]);
    const { successNotification, warningNotification, errorNotification } =
        useDispatchNotification();
    const { t } = useTranslation();
    const translate = (key) => t(`sections.management.section.invoices.${key}`);
    const [isEdit, setIsEdit] = useState(null);
    const [errors, setErrors] = useState({});

    const validateForm = () => {
        let newErrors = {};
        if (!formInvoice.userId)
            newErrors.userId = translate('invoices.invoice.error-user-form');
        if (!formInvoice.paymentMethod)
            newErrors.paymentMethod = translate(
                'invoices.invoice.error-invoice-type-form'
            );
        if (!formInvoice.invoiceType || !formInvoice.invoiceType.value)
            newErrors.errorInovice = translate(
                'invoices.invoice.error-invoice-type-form'
            );
        if (!formInvoice.filePDFUrl)
            newErrors.filePDFUrl = translate('invoices.invoice.error-pdf-form');
        if (formInvoice.invoiceType?.value === 'period') {
            let period = formInvoice.invoicePeriod;
            if (typeof period === 'string' && period.includes('-')) {
                period = period.split('-');
            }
            if (
                !Array.isArray(period) ||
                period.length !== 2 ||
                !period[0] ||
                !period[1]
            ) {
                newErrors.invoicePeriod = translate(
                    'invoices.invoice.error-select-date-form'
                );
            }
        } else if (formInvoice.invoiceType?.value === 'campaigns') {
            if (!formInvoice.campaigns || formInvoice.campaigns.length === 0) {
                newErrors.campaigns = translate(
                    'invoices.invoice.error-select-campaign-form'
                );
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handlePaymentMethod = (paymentMethod) => {
        setFormInvoice({
            ...formInvoice,
            paymentMethod: paymentMethod,
        });
    };

    const resetForm = () => {
        setFormInvoice(initialInvoice);
        if (inputFileRef?.current) inputFileRef.current.value = '';
    };
    const handleInvoiceType = (invoiceType) => {
        const newInvoice = {
            ...formInvoice,
            invoiceType: invoiceType,
        };
        if (invoiceType.id === 1) {
            newInvoice.campaigns = initialInvoice.campaigns;
            newInvoice.campaignsLabel = initialInvoice.campaignsLabel;
        } else {
            newInvoice.invoicePeriod = initialInvoice.invoicePeriod;
            newInvoice.invoicePeriodLabel = initialInvoice.invoicePeriodLabel;
        }
        setFormInvoice(newInvoice);
        // campaigns
    };

    const handlePeriod = (periodValue) => {
        if (periodValue) {
            const label = [
                moment(periodValue[0]).format('YYYY-MM-DDTHH:mm:ss.SSS'),
                moment(periodValue[1]).format('YYYY-MM-DDTHH:mm:ss.SSS'),
            ];
            const value = `${moment(periodValue[0]).format(
                'YYMMDDHH'
            )}-${moment(periodValue[1]).format('YYMMDDHH')}`;

            setFormInvoice({
                ...formInvoice,
                invoicePeriod: value,
                invoicePeriodLabel: label,
            });
        }
    };

    const handleChangeCampaigns = (selectedCampaigns) => {
        setFormInvoice({
            ...formInvoice,
            campaigns: selectedCampaigns.map((campaign) => campaign.value),
            campaignsLabel: selectedCampaigns,
        });
    };

    const handleSelectedUser = (selectedUser) => {
        setFormInvoice({
            ...formInvoice,
            userName: selectedUser.username,
            userId: selectedUser.id,
            userLabel: selectedUser,
        });
    };

    const handleChangeFile = (e) => {
        const isEventDrop = e.dataTransfer?.files;
        const file = isEventDrop ? e.dataTransfer.files[0] : e.target.files[0];
        if (file && file.type !== 'application/pdf') {
            e.target.value = [];
            warningNotification(translate('invoices.invoice.pdf-upload-error'));
            return;
        }
        if (file.size > 513000) {
            e.target.value = [];
            warningNotification(
                translate('invoices.invoice.new-invoice'),
                translate('invoices.invoice.max-size')
            );
            return;
        }
        setFormInvoice({
            ...formInvoice,
            filePDFUrl: URL.createObjectURL(file),
            file: file,
            invoicePdfName: file.name,
        });
    };

    const handleSubmit = () => {
        if (validateForm()) {
            onSubmit()
                .then((res) => {
                    if (!res.error) resetForm();
                })
                .finally(() => {
                    setIsLoading(false);
                    setTimeout(() => {
                        setIsSuccess(false);
                    }, 1000);
                });
        }
    };

    useEffect(() => {
        if (formInvoice.userId) {
            setIsEdit(true);
            getCampaignsByUserID(formInvoice.userId)
                .then((res) => {
                    const campaigns = res.data.map((campaign) => {
                        return {
                            value: campaign.id,
                            label: `#${campaign.id} - ${campaign.name}`,
                            name: campaign.name,
                        };
                    });
                    setCampaignsOptions(campaigns.reverse());
                })
                .catch((err) => {
                    console.log('err: ', err);
                });
        }
    }, [formInvoice.userId]);
    return (
        <>
            <div
                className={`w-full max-w-5xl bg-white bg-opacity-90 pt-4 pl-0 grid grid-cols-1 sm:grid-cols-2 `}>
                <InputWithLabel
                    title={
                        <>
                            {translate('invoices.invoice.username')}
                            <span
                                className="text-xs bg-gray-100 px-1 py-0.5 rounded-full ml-2"
                                style={{
                                    color: '#ea580c',
                                    fontWeight: 'bold',
                                }}>
                                {translate('invoices.invoice.required')}
                            </span>
                        </>
                    }
                    label={translate('invoices.invoice.user-placeholder')}
                    icon="user"
                    idHtmlFor="userName"
                    className={errors.userId ? 'border-red-500' : ''}>
                    <UserSelector
                        idCustom="userName"
                        value={formInvoice.userLabel}
                        onChange={handleSelectedUser}
                    />
                    {errors.userId && (
                        <p className="text-red-500 text-xs">{errors.userId}</p>
                    )}
                </InputWithLabel>
                <InputWithLabel
                    title={
                        <>
                            {translate('invoices.invoice.payment-type')}
                            <span
                                className="text-xs bg-gray-100 px-1 py-0.5 rounded-full ml-2"
                                style={{
                                    color: '#ea580c',
                                    fontWeight: 'bold',
                                }}>
                                {translate('invoices.invoice.required')}
                            </span>
                        </>
                    }
                    label={translate(
                        'invoices.invoice.payment-type-placeholder'
                    )}
                    icon="money-bill"
                    idHtmlFor="paymentMethod"
                    className={errors.paymentMethod ? 'border-red-500' : ''}>
                    <Select
                        inputId="paymentMethod"
                        options={methodOptions}
                        // unica forma de resetear el valor
                        value={
                            formInvoice.paymentMethod
                                ? formInvoice.paymentMethod
                                : null
                        }
                        onChange={handlePaymentMethod}
                        placeholder={translate('invoices.invoice.payment-type')}
                    />
                    {errors.paymentMethod && (
                        <p className="text-red-500 text-xs">
                            {errors.paymentMethod}
                        </p>
                    )}
                </InputWithLabel>
                <InputWithLabel
                    title={
                        <>
                            {translate('invoices.invoice.invoice-type')}
                            <span
                                className="text-xs bg-gray-100 px-1 py-0.5 rounded-full ml-2"
                                style={{
                                    color: '#ea580c',
                                    fontWeight: 'bold',
                                }}>
                                {translate('invoices.invoice.required')}
                            </span>
                        </>
                    }
                    label={translate(
                        'invoices.invoice.invoice-type-placeholder'
                    )}
                    icon="file-invoice-dollar"
                    idHtmlFor="invoiceType"
                    className={errors.errorInovice ? 'border-red-500' : ''}>
                    <Select
                        inputId="invoiceType"
                        options={invoiceTypesOptions}
                        value={
                            formInvoice.invoiceType?.id
                                ? formInvoice.invoiceType
                                : invoiceTypesOptions[1]
                        }
                        onChange={handleInvoiceType}
                        placeholder={translate('invoices.invoice.invoice-type')}
                    />
                </InputWithLabel>
                {formInvoice.invoiceType.value === 'period' ? (
                    <InputWithLabel
                        title={
                            <>
                                {translate('invoices.invoice.period')}
                                <span
                                    className="text-xs bg-gray-100 px-1 py-0.5 rounded-full ml-2"
                                    style={{
                                        color: '#ea580c',
                                        fontWeight: 'bold',
                                    }}>
                                    {translate('invoices.invoice.required')}
                                </span>
                            </>
                        }
                        label={translate('invoices.invoice.period-placeholder')}
                        icon="calendar-alt"
                        idHtmlFor="period">
                        <DateRangePicker
                            clearIcon={null}
                            rangeDivider={t(
                                'common.components.DateRangePicker.until'
                            )}
                            onChange={(periodValue) =>
                                handlePeriod(periodValue)
                            }
                            value={
                                formInvoice.invoicePeriod &&
                                formInvoice.invoicePeriod != ','
                                    ? formInvoice.invoicePeriodLabel
                                    : initialPeriod
                            }
                            // con ancho adaptable
                            style={{ width: '100%' }}
                            calendarClassName="absolute top-10 "
                            className="w-full"
                        />
                        {errors.invoicePeriod && (
                            <p className="text-red-500 text-xs">
                                {errors.invoicePeriod}
                            </p>
                        )}
                    </InputWithLabel>
                ) : (
                    <InputWithLabel
                        title={
                            <>
                                {translate('invoices.invoice.campaigns')}
                                <span
                                    className="text-xs bg-gray-100 px-1 py-0.5 rounded-full ml-2"
                                    style={{
                                        color: '#ea580c',
                                        fontWeight: 'bold',
                                    }}>
                                    {translate('invoices.invoice.required')}
                                </span>
                            </>
                        }
                        label={translate(
                            'invoices.invoice.campaigns-placeholder'
                        )}
                        icon="list-ul"
                        idHtmlFor="campaigns">
                        <Select
                            inputId="campaigns"
                            isMulti={true}
                            options={campaignsOptions}
                            multiple={true}
                            value={formInvoice.campaignsLabel}
                            onChange={handleChangeCampaigns}
                            placeholder={translate(
                                'invoices.invoice.campaign-s'
                            )}
                            className="w-full"
                        />
                        {errors.campaigns && (
                            <p className="text-red-500 text-xs">
                                {errors.campaigns}
                            </p>
                        )}
                    </InputWithLabel>
                )}
                <InputWithLabel
                    title={translate('invoices.invoice.comment')}
                    label={translate('invoices.invoice.comment-placeholder')}
                    icon="comment"
                    idHtmlFor="comment"
                    className={`col-span-1 md:col-span-2 ${
                        errors.comment ? 'border-red-500' : ''
                    }`}>
                    <textarea
                        id="comment"
                        maxLength={255}
                        value={formInvoice.comment || ''}
                        className="w-full h-auto rounded-md border-1 border-gray-300"
                        placeholder={translate('invoices.invoice.comment')}
                        onChange={(e) =>
                            setFormInvoice({
                                ...formInvoice,
                                comment: e.target.value,
                            })
                        }
                    />
                    {errors.comment && (
                        <p className="text-red-500 text-xs">{errors.comment}</p>
                    )}
                </InputWithLabel>
                <InputWithLabel
                    title={
                        <>
                            {translate('invoices.invoice.pdf')}
                            <span
                                className="text-xs bg-gray-100 px-1 py-0.5 rounded-full ml-2"
                                style={{
                                    color: '#ea580c',
                                    fontWeight: 'bold',
                                }}>
                                {translate('invoices.invoice.required')}
                            </span>
                        </>
                    }
                    label={
                        translate('invoices.invoice.pdf-upload-placeholder') +
                        ' (' +
                        translate('invoices.invoice.max-size') +
                        ')'
                    }
                    icon="file-pdf"
                    idHtmlFor="file-to-upload"
                    className="col-span-1 md:col-span-2 mb-4 ">
                    <Dropzone
                        loading={isLoading}
                        inputFileRef={inputFileRef}
                        onDrop={handleChangeFile}
                        disabled={false}>
                        <input
                            type="file"
                            id="file-to-upload"
                            accept=".pdf"
                            ref={inputFileRef}
                            onChange={handleChangeFile}
                            style={{ display: 'none' }}
                        />
                    </Dropzone>
                    {formInvoice.filePDFUrl && (
                        <iframe
                            className="mt-2 w-4/6 h-4/6 pb-8 mx-auto"
                            src={formInvoice.filePDFUrl}
                            title="pdf"
                        />
                    )}
                    {errors.filePDFUrl && (
                        <p className="text-red-500 text-xs">
                            {errors.filePDFUrl}
                        </p>
                    )}
                </InputWithLabel>
            </div>
            <div className="flex w-full max-w-5xl justify-end gap-4 px-4 pr-5 mt-10">
                <button
                    className="capitalize bg-gray-300 text-black rounded transition-colors duration-300 ease-in-out font-light text-sm p-1 py-2 px-2 lg:px-4 focus:outline-none mt-4"
                    onClick={() => {
                        resetForm();
                    }}>
                    {translate('invoices.invoice.reset')}
                </button>
                <CreateButton
                    handleClick={() => {
                        handleSubmit();
                    }}
                    isLoading={isLoading}
                    isSuccess={isSuccess}
                    // isError={isValid}
                    label={translate('invoices.invoice.save')}
                />
            </div>
        </>
    );
};

InvoiceForm.propTypes = {
    onSubmit: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    setIsLoading: PropTypes.func.isRequired,
    isSuccess: PropTypes.bool.isRequired,
    setIsSuccess: PropTypes.func.isRequired,
    setFormInvoice: PropTypes.func.isRequired,
    formInvoice: PropTypes.object.isRequired,
    inputFileRef: PropTypes.object.isRequired,
};

export default InvoiceForm;
